import { CHATBOT_WIDGET_ICON_HEIGHT, CHATBOT_WIDTH } from 'constants/editor';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { WidgetId } from 'types/idTypes';

interface UseRelativePositioning {
  widgetId: WidgetId;

  // Element to position
  elementToPositionRef: RefObject<HTMLDivElement>;

  // Positioning options
  // For now only support bottom or right positioning
  // TODO add top and left support as needed
  placement: 'bottom' | 'right';

  // Options to pass to scrollIntoView
  scrollIntoViewOptions?: ScrollIntoViewOptions;

  // Thresholds for how much space is needed to show the element
  // 'right' placement: threshold[0] = how much space needed on left, threshold[1] = how much space needed on right
  // 'bottom' placement: threshold[0] = how much space needed on top, threshold[1] = how much space needed on bottom
  // If not specified, will use [0, 0]
  // Example:
  // If threshold = [100, 100], then the element will be placed on the right if there is at least 100px of space on the left and 100px of space on the right
  threshold?: [number, number];

  // Start dimension of the element to position - if not specified, will use getBoundingClientRect() to get the dimension
  startDimension?: { width: number; height: number };
}

const SCROLL_THRESHOLD = 0.7;

/**
 * Hook to postion an element relative to a widget
 *
 * Can specify placement (bottom or right) and threshold (how much space is needed to show the element)
 * Currently only supports bottom or right placement.
 * If there is no space available for the elementt in the specifed placement, it will be placed on the opposite side.
 *
 * The element will be positioned when it is mounted. Caller can also reset to the start position by calling resetElementToStartPosition.
 *
 * Note: the positioning of the element is relative to the window, top = 0 and left = 0 being the top left corner of the window
 *
 * @param param0
 * @returns { resetElementToStartPosition }
 */
export const useRelativeWidgetPosition = ({
  widgetId,
  elementToPositionRef,
  placement,
  startDimension,
  threshold,
  scrollIntoViewOptions,
}: UseRelativePositioning) => {
  const widgetElRef = useRef<HTMLElement | null>();

  // Initialize widget element ref
  useEffect(() => {
    widgetElRef.current = document.getElementById(widgetId);
  }, [widgetId]);

  /**
   * Helper to scroll the widget into view if it is outside of the screen or below a certain threshold of the screen height
   */
  const scrollToWidget = useCallback(
    (widgetRect: DOMRect) => {
      const widgetEl = widgetElRef.current;
      if (!widgetEl) return;

      const { x, y, height, width } = widgetRect;
      if (
        y + height > window.innerHeight * SCROLL_THRESHOLD ||
        x > window.innerWidth ||
        x + width > window.innerWidth ||
        y > window.innerHeight ||
        x < 0 ||
        y < 0
      ) {
        widgetEl?.scrollIntoView(scrollIntoViewOptions);
      }
    },
    [scrollIntoViewOptions, widgetElRef],
  );

  /**
   * Places element vertically below or above the widget given the current space
   * Currently, favours bottom placement if there is enough space
   */
  const placeElementVertically = useCallback(
    (shouldScrollToWidget?: boolean) => {
      const widgetEl = widgetElRef.current;
      if (!elementToPositionRef.current) return;
      if (!widgetEl) return;

      const widgetRect = widgetEl.getBoundingClientRect();
      let { x, y, height } = widgetRect;
      const { width: contentWidth, height: contentHeight } = elementToPositionRef.current.getBoundingClientRect();

      const [topThreshold, bottomThreshold] = threshold || [0, 0];

      // Scroll widget into view if the bottom is below a certain threshold of the screen height
      // or widget is outside of the screen
      if (shouldScrollToWidget) {
        scrollToWidget(widgetRect);

        const rect = widgetEl.getBoundingClientRect();
        x = rect.x;
        y = rect.y;
      }

      let top;
      let bottom;
      const left = window.innerWidth - x < contentWidth ? window.innerWidth - contentWidth : x;
      const widgetBottom = y + height;
      const remainingHeight = window.innerHeight - widgetBottom;

      const startHeight = startDimension?.height || contentHeight || 0;

      // Not enough space below or above widget
      if (y < topThreshold && remainingHeight < bottomThreshold) {
        // Not enough space for the initial input without the modal, stick to bottom
        // Otherwise, show below
        if (remainingHeight < startHeight) {
          bottom = 0;
        } else {
          top = widgetBottom;
        }
      } else if (remainingHeight < bottomThreshold) {
        // Not enough space below, show above
        bottom = window.innerHeight - y;
      } else {
        // Show below if there is space
        top = widgetBottom;
      }

      const elementToPosition = elementToPositionRef.current;
      elementToPosition.style.left = `${Math.max(left, 0)}px`;

      bottom !== undefined && (elementToPosition.style.bottom = `${Math.max(bottom, 0)}px`);
      top !== undefined && top && (elementToPosition.style.top = `${Math.max(top, 0)}px`);
    },
    [elementToPositionRef, startDimension?.height, threshold, scrollToWidget, widgetElRef],
  );

  /**
   * Places element on either side of the widget given the current space
   * Currently, favours right placement if there is enough space
   */
  const placeElementHorizontally = useCallback(
    (shouldScrollToWidget?: boolean) => {
      if (!elementToPositionRef.current) return;

      const widgetEl = widgetElRef.current;
      if (!widgetEl) return;
      const widgetRect = widgetEl.getBoundingClientRect();

      let { x, y, width } = widgetRect;
      const { width: contentWidth, height: contentHeight } = elementToPositionRef.current.getBoundingClientRect();

      const [leftThreshold, rightThreshold] = threshold || [0, 0];

      // Scroll widget into view if the bottom is below a certain threshold of the screen height
      // or widget is outside of the screen
      if (shouldScrollToWidget) {
        scrollToWidget(widgetRect);

        const rect = widgetEl.getBoundingClientRect();
        x = rect.x;
        y = rect.y;
      }

      // Position top of modal - should be same as widget unless it will cause overflow, then stick to bottom
      let bottom;
      let top;
      if (window.innerHeight - y < contentHeight) {
        bottom = 0;
      } else {
        top = y;
      }

      let left;
      let right;
      const widgetRight = x + width;
      const remainingWidth = window.innerWidth - widgetRight;
      const startWidth = startDimension?.width || contentWidth || 0;

      // Not enough space on either side of the widget
      if (x < leftThreshold && remainingWidth < rightThreshold) {
        // Not enough space for the initial input, stick to right
        // Otherwise, show to the right
        if (remainingWidth < startWidth) {
          right = 0;
        } else {
          left = widgetRight;
        }
      } else if (remainingWidth < rightThreshold) {
        // Not enough space to the right, show left
        right = window.innerWidth - x;
      } else {
        // Show right if there is space
        left = widgetRight;
      }

      const elementToPosition = elementToPositionRef.current;
      const newRight = (right !== undefined && window.innerWidth - right) || (left ?? 0) + contentWidth;
      const newBottom = (bottom !== undefined && window.innerHeight - bottom) || (top ?? 0) + contentHeight;

      // Check if overlap with the ZD chat widget - if overlap, shift up, otherwise, use pre-calculated position
      if (newRight > window.innerWidth - CHATBOT_WIDTH && newBottom > window.innerHeight - CHATBOT_WIDGET_ICON_HEIGHT) {
        elementToPosition.style.top = 'unset';
        elementToPosition.style.bottom = `${CHATBOT_WIDGET_ICON_HEIGHT}px`;
      } else {
        if (top !== undefined) {
          elementToPosition.style.top = `${Math.max(0, top)}px`;
          elementToPosition.style.bottom = 'unset';
        } else if (bottom !== undefined) {
          elementToPosition.style.bottom = `${Math.max(0, bottom)}px`;
          elementToPosition.style.top = 'unset';
        }
      }

      right !== undefined && (elementToPosition.style.right = `${Math.max(right, 0)}px`);
      left !== undefined && (elementToPosition.style.left = `${Math.max(left, 0)}px`);
    },
    [elementToPositionRef, scrollToWidget, startDimension?.width, threshold, widgetElRef],
  );

  /**
   * Helper to compute the position of the element
   *
   * @param shouldScrollToWidget - if true, scroll to the widget before computing the position
   */
  const resetElementToStartPosition = useCallback(
    (shouldScrollToWidget?: boolean) => {
      if (placement === 'bottom') {
        placeElementVertically(shouldScrollToWidget);
      } else if (placement === 'right') {
        placeElementHorizontally(shouldScrollToWidget);
      }
    },
    [placement, placeElementVertically, placeElementHorizontally],
  );

  // Update element position on mount based on placement prop
  useEffect(() => {
    if (!elementToPositionRef.current) return;

    const widgetEl = widgetElRef.current;
    if (!widgetEl) return;
    resetElementToStartPosition(true);
  }, [widgetElRef, widgetId, elementToPositionRef, resetElementToStartPosition]);

  return { resetElementToStartPosition };
};
